import { Link } from 'expo-router';
import PersonManagerSection from '@/components/Person/PersonManagerSection';
import ActionButton from '@/components/UI/ActionButton';
import Text from '@/components/UI/Text';
import { graphql, type ResultOf } from '@/gql/graphql';
import type { PersonScope } from './PersonScope';

export const GetPersonDetailManagers = graphql(/* GraphQL */ `
  query GetPersonDetailManagers(
    $personId: String!
    $teamWhere: team_bool_exp! = {}
  ) {
    person_manager(
      where: { person_id: { _eq: $personId }, team: $teamWhere }
      order_by: [{ manager: { name: asc } }, { manager_id: asc }]
      limit: 100
    ) {
      person_id
      manager_id
      team_id
      manager {
        id
        name
        color
        avatar_id
      }
    }
    team(
      where: {
        team_people: { person_id: { _eq: $personId } }
        permissions: { _has_key: "edit_person_manager" }
        _and: [$teamWhere]
      }
      limit: 1
    ) {
      id
    }
  }
`);

export default function PersonDetailManagers({
  data,
  personId,
  isManaged,
  scope,
  scopeTitle,
}: {
  data: ResultOf<typeof GetPersonDetailManagers>;
  personId: string;
  isManaged: boolean;
  scope: PersonScope;
  scopeTitle: string;
}) {
  const managers = data.person_manager.flatMap(({ manager }) => {
    if (!manager?.name) return [];
    return [
      {
        id: manager.id,
        name: manager.name,
        color: manager.color,
        avatarId: manager.avatar_id,
      },
    ];
  });
  return (
    <PersonManagerSection
      managers={managers}
      testID="PersonDetailManagers"
      actionPlacement="inline"
      actions={
        isManaged && scope.kind === 'team' && data.team.length > 0 ? (
          <Link
            href={{
              pathname: '/person/[personId]/edit/managers',
              params: {
                personId,
                teamId: scope.id,
                teamTitle: scopeTitle,
              },
            }}
            asChild
          >
            <ActionButton
              forceLabel
              icon="create-outline"
              label="Edit"
              variant="secondary"
              accessibilityLabel="Edit managers"
              testID="PersonDetailManageManagers"
            />
          </Link>
        ) : null
      }
      emptyContent={
        managers.length ? null : (
          <Text size="sm" variant="typographySecondary">
            No managers visible to you.
          </Text>
        )
      }
    />
  );
}
